import { Colors } from "@/enums/Colors";
import { LazoThreadConversationStatus } from "@/enums/LazoThreadConversationStatus";
import { Box, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Typography, styled } from "@mui/material";
import { Conversation } from "./PortalInboxStatistics";

interface Props {
  conversations: Conversation[];
  setSelectedConversation: (c: Conversation) => void;
}

const statusConfig: Record<string, { label: string; color: string; bg: string }> = {
  [LazoThreadConversationStatus.OPEN]: { label: "Open", color: Colors.LAZO_PURPLE, bg: Colors.LAZO_LIGHT_PURPLE },
  [LazoThreadConversationStatus.ANSWERED]: { label: "Answered", color: "#1a7f4e", bg: "#e6f4ed" },
  [LazoThreadConversationStatus.FINALIZED]: { label: "Finalized", color: Colors.LIGHT_GRAY_8A8E93, bg: Colors.LIGHT_GRAY_BG_F6F8FA },
};

const PortalInboxStatisticsTable = ({ conversations, setSelectedConversation }: Props) => {
  return (
    <TableContainer>
      <Table stickyHeader size="small">
        <TableHead>
          <TableRow>
            {["Company", "Lazo user", "Client user", "Status", "Waiting?", "No response", "Time open", "KAM"].map((label) => (
              <StyledHeaderCell key={label}>{label}</StyledHeaderCell>
            ))}
          </TableRow>
        </TableHead>
        <TableBody>
          {conversations.map((row) => {
            const internalMembers = row.members
              .filter((m) => m.internal)
              .map((m) => `${m.firstName} ${m.lastName}`)
              .join(", ");
            const externalMembers = row.members
              .filter((m) => !m.internal)
              .map((m) => `${m.firstName} ${m.lastName}`)
              .join(", ");
            const status = statusConfig[row.status] ?? {
              label: capitalizeWord(row.status),
              color: Colors.LIGHT_GRAY_8A8E93,
              bg: Colors.LIGHT_GRAY_BG_F6F8FA,
            };
            const isFinalized = row.status === LazoThreadConversationStatus.FINALIZED;

            return (
              <StyledRow hover tabIndex={-1} key={row.externalId} onClick={() => setSelectedConversation(row)}>
                <StyledCell>
                  <Typography fontSize="0.8125rem" fontWeight={500} color={Colors.BLACK_151517}>
                    {row.tenant.name}
                  </Typography>
                </StyledCell>
                <StyledCell>
                  <Typography fontSize="0.8125rem" color={Colors.DARKER_DARKER_GRAY}>
                    {internalMembers || "-"}
                  </Typography>
                </StyledCell>
                <StyledCell>
                  <Typography fontSize="0.8125rem" color={Colors.DARKER_DARKER_GRAY}>
                    {externalMembers || "-"}
                  </Typography>
                </StyledCell>
                <StyledCell>
                  <StatusBadge style={{ color: status.color, backgroundColor: status.bg }}>{status.label}</StatusBadge>
                </StyledCell>
                <StyledCell>
                  {row.waitingResponse ? (
                    <StatusBadge style={{ color: "#b45309", backgroundColor: "#fef3c7" }}>Yes</StatusBadge>
                  ) : (
                    <Typography fontSize="0.8125rem" color={Colors.LIGHT_GRAY_8A8E93}>
                      —
                    </Typography>
                  )}
                </StyledCell>
                <StyledCell>
                  <Typography fontSize="0.8125rem" color={row.waitingResponse ? Colors.BLACK_151517 : Colors.LIGHT_GRAY_8A8E93}>
                    {row.waitingResponse ? formatElapsedTime(row.timeWithNoResponse) : "—"}
                  </Typography>
                </StyledCell>
                <StyledCell>
                  <Typography fontSize="0.8125rem" color={isFinalized ? Colors.LIGHT_GRAY_8A8E93 : Colors.BLACK_151517}>
                    {isFinalized ? "—" : formatElapsedTime(row.timeSinceOpen)}
                  </Typography>
                </StyledCell>
                <StyledCell>
                  <Typography fontSize="0.8125rem" color={Colors.DARKER_DARKER_GRAY}>
                    {row.kam?.firstName ? `${row.kam.firstName} ${row.kam.lastName}` : "—"}
                  </Typography>
                </StyledCell>
              </StyledRow>
            );
          })}
        </TableBody>
      </Table>
    </TableContainer>
  );
};

export default PortalInboxStatisticsTable;

const StyledHeaderCell = styled(TableCell)(() => ({
  fontSize: "0.6875rem",
  fontWeight: 600,
  textTransform: "uppercase",
  letterSpacing: "0.06em",
  color: Colors.LIGHT_GRAY_8A8E93,
  backgroundColor: Colors.LIGHT_GRAY_BG_F6F8FA,
  borderBottom: `1px solid ${Colors.LIGHT_GRAY_DEDEE1}`,
  padding: "0.625rem 1rem",
  whiteSpace: "nowrap",
}));

const StyledRow = styled(TableRow)(() => ({
  cursor: "pointer",
  "&:hover": {
    backgroundColor: Colors.LAZO_LIGHT_PURPLE,
  },
  "&:last-child td": {
    borderBottom: "none",
  },
}));

const StyledCell = styled(TableCell)(() => ({
  padding: "0.625rem 1rem",
  borderBottom: `1px solid ${Colors.LIGHT_GRAY_DEDEE1}`,
  verticalAlign: "middle",
}));

const StatusBadge = styled(Box)(() => ({
  display: "inline-flex",
  alignItems: "center",
  borderRadius: "50px",
  padding: "0.15rem 0.6rem",
  fontSize: "0.75rem",
  fontWeight: 600,
  whiteSpace: "nowrap",
}));

function formatElapsedTime(diff: number): string {
  const days = Math.floor(diff / (1000 * 60 * 60 * 24));
  const hours = Math.floor((diff / (1000 * 60 * 60)) % 24);
  const minutes = Math.floor((diff / (1000 * 60)) % 60);

  const parts: string[] = [];
  if (days > 0) parts.push(`${days}d`);
  if (hours > 0) parts.push(`${hours}h`);
  if (minutes > 0 || parts.length === 0) parts.push(`${minutes}m`);

  return parts.join(" ");
}

export const capitalizeWord = (word: string): string => {
  if (!word) return "";
  const lower = word.toLowerCase();
  return lower.charAt(0).toUpperCase() + lower.slice(1);
};
